import { isAxiosError } from 'axios';
import apiClient from './client';

export interface LenderProduct {
  name: string;
  urn: string;
  active?: boolean;
  integrations?: Partial<Record<DocumentLos, { reportDelivery?: { document?: Record<string, string | null> } }>>;
  reportPolicy?: LenderProductReportPolicy;
}

export type DocumentLos = 'encompass' | 'meridianlink' | 'meridianlink-mortgage';

export type ReportDataPoint =
  | 'censusTrackNumber' | 'floodZone' | 'certificateNumber' | 'floodZoneCode'
  | 'communityName' | 'msaNumber' | 'communityNumber' | 'panelNumber'
  | 'countyCode' | 'stateCode' | 'effectiveRevisedDate'
  | 'propertyTaxId' | 'legalDescription' | 'vestingDescription'
  | 'estimatedValue' | 'valuationDate' | 'confidenceScore' | 'fsdScore'
  | 'minimumValue' | 'maximumValue';

export interface LenderProductReportPolicy {
  dataPoints: ReportDataPoint[];
  coverage: { states: string[] };
}

export interface UpdateProductConfigurationRequest {
  productUrn: string;
  documents?: Partial<Record<DocumentLos, Record<string, string>>>;
  reportPolicy?: LenderProductReportPolicy;
}

export interface Lender {
  urn: string;
  uuid: string;
  name: string;
  displayName: string;
  status?: 'active' | 'blocked';
  salesforceId?: string;
  nmlsId?: string;
  /** Urn of one of the lender's users. */
  primaryContact?: string;
  address?: LenderAddress;
  products: (LenderProduct | string)[];
  credentials: LenderCredential[];
  integrations?: Record<string, unknown>;
  branding?: Record<string, unknown>;
  creationDate: string;
  modificationDate: string;
}

export interface LenderAddress {
  street?: string;
  city?: string;
  /** Two-letter code. */
  state?: string;
  zip?: string;
}

export interface LenderCredential {
  name?: string;
  key: string;
  secret?: string;
  value?: string;
}

export interface LenderUserProduct {
  name: string;
  urn: string;
}

export interface LenderUser {
  urn: string;
  uuid: string;
  /** Holds the email, not the person's name. */
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role?: string;
  /**
   * Stored, not inferred: the user has every active product of its lender, including the ones
   * added after. `products` still lists them — it carries the order thresholds — so a user
   * holding the whole catalog is NOT the same thing as a user who inherits it (OM-4022).
   * Absent on users the OM-4614 migration left restricted.
   */
  inheritsProducts?: boolean;
  products?: LenderUserProduct[];
  lender?: string;
  privileges?: string[];
  /** Same vocabulary as lenders: blocked surfaces as Inactive in the UI. */
  status?: 'active' | 'blocked';
  /** ISO timestamp of the user's last activity; null/absent when never seen. */
  lastActive?: string | null;
  creationDate: string;
  modificationDate: string;
}

export interface AddLenderUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  mlConsumerUserName?: string;
  mlMortgageUserName?: string;
  /** null = every product of the lender; otherwise only the urns set true. */
  productAccess: Record<string, boolean> | null;
}

/** One user of an imported file: every imported user gets every product of the lender. */
export type ImportedLenderUser = Omit<AddLenderUserRequest, 'productAccess'>;

/**
 * The console's Edit User (OM-4023/OM-4022). Every field is optional: the drawer sends only what
 * it owns, and an absent field is left as it is rather than blanked. The email is not editable —
 * it is half of the user's urn and of every LOS identity built from it.
 */
export interface UpdateLenderUserRequest {
  firstName?: string;
  lastName?: string;
  role?: string;
  /** null = every product of the lender; a record = only the urns set true; absent = unchanged. */
  productAccess?: Record<string, boolean> | null;
  /** Empty or null removes the user's access through that LOS; absent leaves it as it is. */
  mlConsumerUserName?: string | null;
  mlMortgageUserName?: string | null;
  /** Cascade (order logic rule) id -> enabled. Absent leaves every membership as it is. */
  cascadeAccess?: Record<string, boolean>;
}

/**
 * A lender user as the Edit User drawer reads it, and as a save answers it. Unlike the users
 * table's row, this carries `productAccess` straight from the server, so the drawer never has to
 * guess whether the user inherits the lender's catalog or is restricted to part of it.
 */
export interface LenderUserDetail {
  urn: string;
  uuid: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role?: string;
  /** null = every active product of the lender, including ones added later. */
  productAccess: Record<string, boolean> | null;
  /** Only when the lender has that LOS and the user a login there. */
  mlConsumerUserName?: string;
  mlMortgageUserName?: string;
}

/**
 * One of the lender's cascades, with whether the user being edited can reach it. `id` is the
 * order logic rule's id as the server reports it — not a uuid.
 */
export interface LenderUserCascade {
  id: string;
  name: string;
  enabled: boolean;
  granted: boolean;
}

export interface CreateLenderRequest {
  name: string;
  displayName: string;
  products?: string[];
  credentials?: LenderCredential[];
  integrations?: Record<string, unknown>;
}

export interface UpdateLenderRequest {
  displayName?: string;
  /** '' clears it, as for nmlsId and primaryContact. */
  salesforceId?: string;
  nmlsId?: string;
  primaryContact?: string;
  address?: LenderAddress;
  products?: string[];
  credentials?: LenderCredential[];
  integrations?: Record<string, unknown>;
  [key: string]: unknown;
}

function withActiveProducts(lender: Lender): Lender {
  return { ...lender, products: lender.products?.filter((product) => typeof product === 'string' || product.active !== false) ?? [] };
}

export async function getLenders(): Promise<Lender[]> {
  const response = await apiClient.get<Lender[]>('/oms/lenders');
  return response.data.map(withActiveProducts);
}

export type LenderLos = 'encompass' | 'meridianlink' | 'meridianlink-mortgage';

export interface LendersListItem {
  urn: string;
  displayName: string;
  entityId: string | null;
  los: LenderLos[];
  userCount: number;
  productCount: number;
  status: 'active' | 'blocked';
  creationDate: string | null;
}

export type LendersListSort = 'lender' | 'entityId' | 'los' | 'users' | 'products' | 'status' | 'created';

export interface LendersListParams {
  offset: number;
  limit: number;
  sort: LendersListSort;
  order: 'asc' | 'desc';
  search?: string;
}

export interface LendersListResponse {
  items: LendersListItem[];
  total: number;
  offset: number;
  limit: number;
}

export async function getLendersList(params: LendersListParams): Promise<LendersListResponse> {
  const response = await apiClient.get<LendersListResponse>('/oms/lenders/list', { params });
  return response.data;
}

export async function getLender(urn: string): Promise<Lender> {
  const response = await apiClient.get<Lender>(`/oms/lenders/${urn}`);
  return withActiveProducts(response.data);
}

export async function createLender(data: CreateLenderRequest): Promise<Lender> {
  const response = await apiClient.post('/oms/lenders', data);
  return response.data;
}

export async function updateLender(urn: string, data: UpdateLenderRequest): Promise<Lender> {
  const response = await apiClient.patch(`/oms/lenders/${urn}`, data);
  return response.data;
}

export async function getLenderProducts(urn: string): Promise<string[]> {
  const response = await apiClient.get(`/oms/lenders/${urn}/products`);
  return response.data;
}

export async function updateLenderProductConfiguration(
  urn: string,
  data: UpdateProductConfigurationRequest,
): Promise<void> {
  await apiClient.patch(`/oms/lenders/${encodeURIComponent(urn)}/products/configuration`, data);
}

/** Deactivates this lender assignment while preserving historical report configuration. */
export async function removeLenderProduct(urn: string, productUrn: string): Promise<void> {
  await apiClient.patch(`/oms/lenders/${encodeURIComponent(urn)}/products/removal`, { productUrn });
}

export async function getLenderCredential(urn: string, key: string): Promise<LenderCredential> {
  const response = await apiClient.get(`/oms/lenders/${urn}/credentials/${key}`);
  return response.data;
}

export async function blockLender(urn: string): Promise<void> {
  await apiClient.patch(`/oms/lenders/${urn}/block`);
}

export async function unblockLender(urn: string): Promise<void> {
  await apiClient.patch(`/oms/lenders/${urn}/unblock`);
}

export async function deleteLender(urn: string, confirmUrn: string): Promise<void> {
  await apiClient.delete(`/oms/lenders/${urn}`, { data: { confirmUrn } });
}

/** The lender's profile picture, or null when none was uploaded. */
export async function getLenderLogo(urn: string): Promise<Blob | null> {
  try {
    const response = await apiClient.get<Blob>(`/oms/lenders/${encodeURIComponent(urn)}/logo`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export async function uploadLenderLogo(urn: string, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file);
  await apiClient.post(`/oms/lenders/${encodeURIComponent(urn)}/logo`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function getLenderUsers(urn: string): Promise<LenderUser[]> {
  const response = await apiClient.get(`/oms/lenders/${urn}/users`);
  return response.data;
}

export async function addLenderUser(urn: string, data: AddLenderUserRequest): Promise<LenderUser> {
  const response = await apiClient.post(`/oms/lenders/${encodeURIComponent(urn)}/users`, data);
  return response.data;
}

/** One lender user as the Edit User drawer needs it, product grants resolved by the server. */
export async function getLenderUser(urn: string, userUrn: string): Promise<LenderUserDetail> {
  const response = await apiClient.get(
    `/oms/lenders/${encodeURIComponent(urn)}/users/${encodeURIComponent(userUrn)}`,
  );
  return response.data;
}

export async function updateLenderUser(
  urn: string,
  userUrn: string,
  data: UpdateLenderUserRequest,
): Promise<LenderUserDetail> {
  const response = await apiClient.patch(
    `/oms/lenders/${encodeURIComponent(urn)}/users/${encodeURIComponent(userUrn)}`,
    data,
  );
  return response.data;
}

/**
 * The lender's cascades with this user's membership of each — the Cascades section of the Product
 * Access panel. Failure answers an empty list: a cascade list that cannot load must not be read as
 * "this user has no cascades" and silently revoke them on save, so the section simply does not
 * render and no cascadeAccess is sent.
 */
export async function getLenderUserCascades(urn: string, userUrn: string): Promise<LenderUserCascade[]> {
  try {
    const response = await apiClient.get(
      `/oms/lenders/${encodeURIComponent(urn)}/users/${encodeURIComponent(userUrn)}/cascades`,
    );
    return response.data ?? [];
  } catch {
    return [];
  }
}

/** Adds every user or none: a 400 or 409 lists the emails at fault in `emails`. */
export async function importLenderUsers(urn: string, users: ImportedLenderUser[]): Promise<{ imported: number }> {
  const response = await apiClient.post(`/oms/lenders/${encodeURIComponent(urn)}/users/import`, { users });
  return response.data;
}
